import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { DashboardDateConfig } from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  computeCustomRelativeRange,
  computeDateFromComparisonPreset,
  computeDateFromPreset,
  CustomPresetState,
  INIT_CUSTOM_PRESET_STATE,
  ValidCustomPresetState
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { toPickerRange } from './date-range';

// A dashboard's saved date window is the PICKER'S OWN STATE, not a resolved range, so a dashboard
// saved on a preset re-resolves it every load: "MTD" keeps meaning this month, and a fiscal preset
// keeps meaning whatever this company's fiscal calendar says. Only a hand-picked window (no preset)
// stores concrete dates and stays put.
//
// Everything here resolves through bpm-types' own preset functions. There is deliberately no second
// implementation of the fiscal calendar in this file, and there should never be one.

// A custom preset is only resolvable once its `number` is filled in — the picker's initial state
// leaves it undefined while the user is still choosing.
const isResolvable = (state: CustomPresetState | undefined): state is ValidCustomPresetState =>
  state !== undefined && state.number !== undefined;

const bothEnds = (start: string | null | undefined, end: string | null | undefined) =>
  start && end ? { start, end } : undefined;

// A config carrying no primary selection — the slots the picker requires, left empty. Only reached
// when a comparison change is recorded before any primary change has been.
const emptyConfig = (): DashboardDateConfig => ({
  start: null,
  end: null,
  selectedPreset: null,
  startCustomPreset: { ...INIT_CUSTOM_PRESET_STATE },
  endCustomPreset: { ...INIT_CUSTOM_PRESET_STATE }
});

// Fold what the picker just reported into what's already stored. The picker reports its primary and
// comparison halves through SEPARATE callbacks that can both fire on a single Apply, so these merge
// into the previous config rather than composing a whole one from a single callback's arguments —
// otherwise whichever fires second overwrites the other's half with stale values.
export const withPrimarySelection = (
  previous: DashboardDateConfig | undefined,
  range: DateRange,
  selection: {
    selectedPreset: string | null;
    startCustomPreset: CustomPresetState;
    endCustomPreset: CustomPresetState;
  }
): DashboardDateConfig => ({
  ...(previous ?? emptyConfig()),
  start: range.start,
  end: range.end,
  selectedPreset: selection.selectedPreset,
  startCustomPreset: selection.startCustomPreset,
  endCustomPreset: selection.endCustomPreset
});

export const withComparisonSelection = (
  previous: DashboardDateConfig | undefined,
  range: DateRange,
  selection: {
    selectedCompPerType: string | null;
    startCompCustomPreset: CustomPresetState;
    endCompCustomPreset: CustomPresetState;
  }
): DashboardDateConfig => ({
  ...(previous ?? emptyConfig()),
  comparisonStart: range.start,
  comparisonEnd: range.end,
  selectedCompPerType: selection.selectedCompPerType,
  startCompCustomPreset: selection.startCompCustomPreset,
  endCompCustomPreset: selection.endCompCustomPreset
});

// Resolve a saved config to the concrete window(s) a dashboard opens on. A preset that no longer
// resolves — a renamed label, a removed option, a calendar that has since changed — yields undefined
// for that half, and the caller falls back to its own default rather than rendering a broken window.
export const resolveDashboardDateConfig = (
  config: DashboardDateConfig | undefined,
  fiscalCal: CompanyFiscalCal
): { range: DateRange | undefined; comparisonRange: DateRange | undefined } => {
  if (!config) return { range: undefined, comparisonRange: undefined };

  let range: DateRange | undefined;
  try {
    if (config.selectedPreset === 'Custom') {
      range =
        isResolvable(config.startCustomPreset) && isResolvable(config.endCustomPreset)
          ? computeCustomRelativeRange(config.startCustomPreset, config.endCustomPreset, fiscalCal)
          : undefined;
    } else if (config.selectedPreset) {
      range = computeDateFromPreset(config.selectedPreset, fiscalCal);
    }
  } catch {
    range = undefined;
  }
  // No preset — or one that didn't resolve — falls back to the dates stored alongside it, which is
  // the normal path for a hand-picked window.
  range = range ?? bothEnds(config.start, config.end);

  let comparisonRange: DateRange | undefined;
  try {
    if (config.selectedCompPerType === 'Custom') {
      comparisonRange =
        isResolvable(config.startCompCustomPreset) && isResolvable(config.endCompCustomPreset)
          ? computeCustomRelativeRange(
              config.startCompCustomPreset,
              config.endCompCustomPreset,
              fiscalCal
            )
          : undefined;
    } else if (config.selectedCompPerType) {
      // A comparison preset is defined RELATIVE to the primary window ("the prior period"), so it
      // resolves against the primary — including the trailing default, for a config that carries no
      // primary of its own. That happens for real: the picker reports its two halves through
      // separate callbacks, so changing ONLY the comparison window saves a config with a comparison
      // preset and an empty primary. Gating this on a resolved primary would leave that config
      // replaying the concrete dates it was saved with instead of re-resolving. Both this and the
      // provider fall back through toPickerRange(undefined), so they cannot disagree about it.
      const effectivePrimary = range ?? toPickerRange(undefined);
      comparisonRange = computeDateFromComparisonPreset(
        config.selectedCompPerType,
        effectivePrimary.start,
        effectivePrimary.end,
        fiscalCal
      );
    }
  } catch {
    comparisonRange = undefined;
  }

  return {
    range,
    comparisonRange: comparisonRange ?? bothEnds(config.comparisonStart, config.comparisonEnd)
  };
};

// Field-by-field, not `===`: `useDashboard` has no `select` transform, so a background refetch (tab
// focus, post-save invalidation) mints a brand-new `defaultDateConfig` object with the SAME values
// while the view provider stays mounted. Reference equality would treat that as a real edit and start
// writing date params into the URL after nothing but a tab-away, silently freezing a rolling shared
// link into a snapshot. Lives here rather than in the provider because it is pure logic over a
// DashboardDateConfig, which is what this module is for -- and so it can be tested directly.
export const customPresetStatesEqual = (
  a: CustomPresetState | undefined,
  b: CustomPresetState | undefined
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.number === b.number &&
    a.interval === b.interval &&
    a.before === b.before &&
    a.referenceDate === b.referenceDate
  );
};

// undefined counts as equal to undefined; anything else compares every field the picker can set,
// including both nested CustomPresetState halves. The comparison half coalesces through `?? null`
// because those fields are optional and a config that round-trips through JSON comes back with the
// key absent rather than explicitly undefined -- the two must not read as a difference.
export const dashboardDateConfigsEqual = (
  a: DashboardDateConfig | undefined,
  b: DashboardDateConfig | undefined
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.start === b.start &&
    a.end === b.end &&
    a.selectedPreset === b.selectedPreset &&
    customPresetStatesEqual(a.startCustomPreset, b.startCustomPreset) &&
    customPresetStatesEqual(a.endCustomPreset, b.endCustomPreset) &&
    (a.comparisonStart ?? null) === (b.comparisonStart ?? null) &&
    (a.comparisonEnd ?? null) === (b.comparisonEnd ?? null) &&
    (a.selectedCompPerType ?? null) === (b.selectedCompPerType ?? null) &&
    customPresetStatesEqual(a.startCompCustomPreset, b.startCompCustomPreset) &&
    customPresetStatesEqual(a.endCompCustomPreset, b.endCompCustomPreset)
  );
};
